import { View, Text, Pressable, type StyleProp, type ViewStyle } from 'react-native';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { Card } from './Card';
import { Money } from './Money';

interface StatCardProps {
  label: string;
  value: string;
  subtitle?: string;
  /** Tailwind color class for the value text. Default: positive money green. */
  valueColor?: string;
  /** Optional extra content below subtitle (e.g., a day-change line). */
  footer?: ReactNode;
  footerPlacement?: 'bottom' | 'aside';
  /** When provided, the card becomes tappable and shows a hover state on web. */
  onPress?: () => void;
  /** Use row for equal-width horizontal cards, full for stacked/full-width cards. */
  layout?: 'row' | 'full';
  /** Minimum card height in native points. */
  minHeight?: number;
  containerClassName?: string;
  containerStyle?: StyleProp<ViewStyle>;
}

export function StatCard({
  label,
  value,
  subtitle,
  valueColor = 'text-forest-600 dark:text-green-500',
  footer,
  footerPlacement = 'bottom',
  onPress,
  layout = 'row',
  minHeight,
  containerClassName,
  containerStyle,
}: StatCardProps) {
  const resolvedMinHeight = minHeight ?? (footer && footerPlacement === 'bottom' ? 112 : 96);
  const wrapperStyle: StyleProp<ViewStyle> = [
    layout === 'row'
      ? { flexBasis: 0, flexGrow: 1, flexShrink: 1, minWidth: 0 }
      : { width: '100%' },
    containerStyle,
  ];

  const content = (
    <Card
      className="justify-between"
      style={{ minHeight: resolvedMinHeight }}
    >
      <View className={footer && footerPlacement === 'aside' ? 'flex-row items-center justify-between gap-4' : ''}>
        <View className={footer && footerPlacement === 'aside' ? 'flex-1 min-w-0' : undefined}>
          <Text
            className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3 mb-2"
            numberOfLines={1}
          >
            {label}
          </Text>
          <Money
            weight={500}
            className={`text-[22px] ${valueColor}`}
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.75}
          >
            {value}
          </Money>
          {subtitle && (
            <Text
              className="text-[11.5px] text-ink-3 dark:text-ink-dark-3 mt-1"
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.85}
            >
              {subtitle}
            </Text>
          )}
        </View>
        {footer && footerPlacement === 'aside' ? (
          <View className="items-end shrink-0 max-w-[48%]">
            {footer}
          </View>
        ) : null}
      </View>
      {footer && footerPlacement === 'bottom' ? <View className="mt-1">{footer}</View> : null}
    </Card>
  );

  if (!onPress) {
    return (
      <View className={twMerge('min-w-0', containerClassName)} style={wrapperStyle}>
        {content}
      </View>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      className={twMerge('min-w-0 web-card-hover active:opacity-80', containerClassName)}
      style={wrapperStyle}
    >
      {content}
    </Pressable>
  );
}
